import {
  countrySchemaRegistry,
  getCountryName,
  type Country,
  type CountryName,
} from '@tectonics/prs/country';
import type { PaymentSpecGenerator } from '@tectonics/prs/payment-spec/generator';
import { buildPaymentSpecGenerator } from '@tectonics/prs/payment-spec/generator/build-payment-spec-generator';
import { loadModuleWithRetry } from '@tectonics/utils/modules';

type PaymentSpecGeneratorModule = { default?: PaymentSpecGenerator };

export const throwNoCountrySpecGenerator = (countryName: CountryName): never => {
  throw new Error(`Failed to load country spec generator for "${countryName}".`);
};

function getSchemaSpecGenerator(country: Country): PaymentSpecGenerator | null {
  const countrySchema = countrySchemaRegistry.get(country);

  return countrySchema ? buildPaymentSpecGenerator({ countrySchema }) : null;
}

export function loadCountrySpecGeneratorSync(country: Country): PaymentSpecGenerator {
  const schemaGenerator = getSchemaSpecGenerator(country);
  if (schemaGenerator) return schemaGenerator;

  const countryName = getCountryName(country);
  const countryFolder = countryName.replace(/ /g, '-');

  const { default: generator } = require(
    `@tectonics/prs/${countryFolder}/payment-spec-generator.js`,
  ) as PaymentSpecGeneratorModule;

  if (!generator) throw throwNoCountrySpecGenerator(countryName);

  return generator;
}

export async function loadCountrySpecGenerator(country: Country): Promise<PaymentSpecGenerator> {
  const schemaGenerator = getSchemaSpecGenerator(country);
  if (schemaGenerator) return schemaGenerator;

  const countryName = getCountryName(country);
  const countryFolder = countryName.replace(/ /g, '-');

  return loadModuleWithRetry<PaymentSpecGenerator>(
    () => import(`@tectonics/prs/${countryFolder}/payment-spec-generator.js`),
    () => throwNoCountrySpecGenerator(countryName)
  );
}
